import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { bumpImageSpaceUsageCounts, cropImageAPI } from '@/utils/api';
import { IPE_UPLOAD_ACCEPT } from '@/utils/ipeImageUploadFormats';
import {
  filterPublishVideoLibraryRows,
} from '@/pages/ipe/workbench/utils/videoSpaceLibraryPick';
import { getImageFromIndexedDB } from '@/utils/imageStorage';
import { resolveSignedDisplayUrl } from '@/utils/imageUrlUtils';
import WorkbenchPreviewImage from '@/components/ipe/WorkbenchPreviewImage';
import IpeLazyListSentinel from '@/components/ipe/IpeLazyListSentinel';
import {
  buildImageSpaceLibraryModalSourceOptions,
  resolveImageSpaceSourceKey,
} from '@/pages/ipe/workbench/constants/imageSpaceSourceOptions';
import IpeToolbarSelect from '@/pages/components/IpeToolbarSelect';
import { uploadImageSpaceFiles } from '@/pages/ipe/workbench/utils/imageSpaceUploadFlow';
import { handleIpeError } from '@/pages/ipe/workbench/utils/errorHandler';
import LibraryModalImageCard from '@/pages/ipe/workbench/components/LibraryModalImageCard';
import LibraryModalDateGroupedGrid from '@/pages/ipe/workbench/components/LibraryModalDateGroupedGrid';
import LibraryModalDateFilter from '@/pages/ipe/workbench/components/LibraryModalDateFilter';
import ImageSpacePreviewModal from '@/pages/ipe/workbench/components/ImageSpacePreviewModal';
import VideoSpacePreviewModal from '@/pages/ipe/workbench/components/VideoSpacePreviewModal';
import { filterItemsByDateRange } from '@/pages/ipe/workbench/utils/groupGalleryByDate';
import {
  resolveGoodsPrintUrl,
  pickImageSpaceDisplayUrl,
  parseOriginalImageIdFromCropSpaceId,
} from '@/pages/ipe/workbench/utils/goodsPrintUrlResolve';
import { sanitizeImageSpaceRecords } from '@/pages/ipe/workbench/panels/imageSpaceUtils';
import { filterImageSpaceByWorkspaceMode } from '@/pages/ipe/workbench/utils/imageSpaceMeta';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import { useTranslation } from 'react-i18next';
import {
  IMAGE_SPACE_PAGE_SIZE,
  fetchImageSpacePage,
  normalizeImageSpacePage,
  appendUniqueImageSpaceRows,
} from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import {
  VIDEO_SPACE_LIST_PAGE_SIZE,
  fetchVideoSpacePage,
  normalizeVideoSpacePage,
} from '@/pages/ipe/workbench/utils/videoSpacePageFetch';
import { filterVideoSpaceByWorkspaceMode } from '@/pages/ipe/workbench/utils/videoSpaceMeta';
import { useArmedScrollLoadMore } from '@/hooks/useArmedScrollLoadMore';
import { resolveStampSlotNames } from '@/pages/ipe/workbench/utils/podTemplateMeta';

import {
  warmDisplayCacheFromRecords,
} from '@/utils/imageDisplayAccess';
import {
  createLibraryImageLoadGuard,
  shouldApplyRecoveredImageUrl,
} from '@/pages/ipe/workbench/utils/libraryModalImageLoadGuard';

const getModalSelectedIds = (positionArray) =>
  positionArray.filter((id) => id !== null && id !== undefined);

/** 默认空引用必须稳定：`prop = []` 每次渲染都是新数组，会把依赖它的 effect 打成 Maximum update depth */
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

const computeLibrarySelectionLimit = (positionArray, maxSelection, outsideSelectedIds) => {
  const modalIds = getModalSelectedIds(positionArray);
  const modalCount = modalIds.length;

  if (maxSelection == null || !Number.isFinite(Number(maxSelection)) || Number(maxSelection) <= 0) {
    return { modalCount, effectiveTotal: modalCount, canSelectMore: true, displayMax: null };
  }

  const limit = Number(maxSelection);
  const outsideIds = (outsideSelectedIds || []).map(String).filter(Boolean);
  const outsideSet = new Set(outsideIds);

  if (outsideSet.size === 0) {
    return {
      modalCount,
      effectiveTotal: modalCount,
      canSelectMore: modalCount < limit,
      displayMax: limit,
    };
  }

  const overlap = modalIds.filter((id) => outsideSet.has(String(id))).length;
  const effectiveTotal = outsideSet.size - overlap + modalCount;
  return {
    modalCount,
    effectiveTotal,
    canSelectMore: effectiveTotal < limit,
    displayMax: limit,
  };
};

export default function LibraryModalContent({
  isOpen,
  onClose,
  images,
  selection,
  onToggle,
  onConfirm, // (positionArray, stampGroups) => void
  /** @deprecated 优先使用 onUploadComplete */
  onUpload,
  /** 上传成功后合并进图片空间列表 */
  onUploadComplete,
  /** 弹窗分页加载到的行同步给父级（默认回退 onUploadComplete） */
  onImagesAppended,
  uploadOriginSource = 'upload',
  uploadFolderId = '',
  creatorPayload = EMPTY_OBJECT,
  showToast,
  selectedImageTemplates = EMPTY_ARRAY, // 主图创建模式：选择的模版
  fromCropTask = null, // 如果图片来自裁剪任务，传递任务ID
  /** 图片空间原图 ID → 实际印花图 URL（如智能抠图结果） */
  printUrlOverrides = null,
  /** 使用次数已写入服务端后的增量（id -> 次数），用于列表就地刷新 */
  onUsageBumped,
  /** 为 true 时不在这里累加使用次数（由父级在「创建任务」等时机统一累加，避免主图创建重复计数） */
  deferUsageBump = false,
  selectAllLabel = '',
  panelClassName = '',
  /** 该功能允许的图片总数上限（可与 outsideSelectedIds 组合计算） */
  maxSelection = null,
  /** 弹窗外已占用的图片 ID（如已上传/已添加的参考图） */
  outsideSelectedIds = EMPTY_ARRAY,
  /** 达到上限时的回调（如 toast 提示） */
  onSelectionLimitReached = null,
  authUserId = '',
  authDisplayName = '',
  /** 商品设计选模板后：未选印花前禁止取消/点遮罩关闭 */
  requireSelectionBeforeClose = null,
  /** 为 false 时不可点遮罩 / Esc 关闭，只能点内部取消或确认 */
  closeOnBackdropClick = true,
  /**
   * mediaKind:
   * - image（默认）：图片空间
   * - video：视频空间，并按本地主图视频同样的格式过滤（mp4/mov 等）
   */
  mediaKind = 'image',
}) {
  const { t } = useTranslation('workbench');
  const isVideoLibrary = mediaKind === 'video';
  const workspaceMode = useIpeWorkspaceMode();
  const [imageUrlMap, setImageUrlMap] = useState({});
  const imageUrlMapRef = useRef(imageUrlMap);
  imageUrlMapRef.current = imageUrlMap;
  const imageLoadGuardRef = useRef(null);
  if (!imageLoadGuardRef.current) {
    imageLoadGuardRef.current = createLibraryImageLoadGuard();
  }
  const [failedImageIds, setFailedImageIds] = useState(() => new Set());
  const [previewTarget, setPreviewTarget] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [stampGroups, setStampGroups] = useState([]); // 印花分组
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  // 位置数组：每个位置对应一个图片ID或null，用于保持位置不变
  const [positionArray, setPositionArray] = useState([]);
  const bodyRef = useRef(null);
  const [gridScrollRoot, setGridScrollRoot] = useState(null);
  const loadMoreSentinelRef = useRef(null);
  const pageRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const [pagedImages, setPagedImages] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  const syncAppendedToParent = useCallback((rows, meta = null) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      if (meta && typeof onImagesAppended === 'function') {
        onImagesAppended([], meta);
      }
      return;
    }
    const notify = typeof onImagesAppended === 'function'
      ? onImagesAppended
      : onUploadComplete;
    notify?.(rows, meta || undefined);
  }, [onImagesAppended, onUploadComplete]);

  const displayImages = useMemo(() => {
    const uniqueById = (list) => {
      const seen = new Set();
      const out = [];
      (Array.isArray(list) ? list : []).forEach((item) => {
        const id = item?.id != null ? String(item.id) : '';
        if (!id || seen.has(id)) return;
        seen.add(id);
        out.push(item);
      });
      return out;
    };
    const propList = uniqueById(images);
    if (!pagedImages.length) {
      return isVideoLibrary ? filterPublishVideoLibraryRows(propList) : propList;
    }
    const pagedIds = new Set(
      pagedImages.map((item) => String(item?.id || '')).filter(Boolean),
    );
    const extras = propList.filter((item) => {
      const id = item?.id != null ? String(item.id) : '';
      return id && !pagedIds.has(id);
    });
    const merged = uniqueById(extras.length > 0 ? [...extras, ...pagedImages] : pagedImages);
    return isVideoLibrary ? filterPublishVideoLibraryRows(merged) : merged;
  }, [images, pagedImages, isVideoLibrary]);

  const sourceFilteredImages = useMemo(() => {
    const list = Array.isArray(displayImages) ? displayImages : [];
    const sourceKey = String(sourceFilter || '').trim();
    if (!sourceKey) return list;
    return list.filter((image) => resolveImageSpaceSourceKey(image) === sourceKey);
  }, [displayImages, sourceFilter]);

  const filteredImages = useMemo(
    () => filterItemsByDateRange(sourceFilteredImages, { startDate: filterStartDate, endDate: filterEndDate }),
    [sourceFilteredImages, filterStartDate, filterEndDate],
  );

  const resolveRawImageUrl = useCallback((image) => {
    const override = resolveGoodsPrintUrl(image?.id, printUrlOverrides || {});
    if (override) return override;
    if (imageUrlMap[image?.id]) return imageUrlMap[image.id];
    return pickImageSpaceDisplayUrl(image) || image?.url || image?.src || '';
  }, [imageUrlMap, printUrlOverrides]);

  const resolveThumbUrl = (imageId, image) => {
    const override = resolveGoodsPrintUrl(imageId, printUrlOverrides || {});
    if (override) return override;
    if (imageUrlMap[imageId]) return imageUrlMap[imageId];
    const fromImage = pickImageSpaceDisplayUrl(image);
    if (fromImage) return fromImage;
    return '';
  };

  const applyImageRowToUrlMap = (image, urlMap) => {
    if (!image?.id) return;
    const rowUrl = pickImageSpaceDisplayUrl(image);
    if (rowUrl) urlMap[image.id] = rowUrl;
  };

  useEffect(() => {
    if (isOpen) {
      imageLoadGuardRef.current.reset();
      setFailedImageIds(new Set());
      setImageUrlMap({});
      setFilterStartDate('');
      setFilterEndDate('');
      setSourceFilter('');
      setPreviewTarget(null);
    }
  }, [isOpen, printUrlOverrides]);

  useEffect(() => {
    if (!isOpen) {
      pageRef.current = 0;
      loadingMoreRef.current = false;
      setPagedImages((prev) => (prev.length === 0 ? prev : []));
      setListTotal((prev) => (prev === 0 ? prev : 0));
      setHasMore((prev) => (prev === false ? prev : false));
      setLoadingMore((prev) => (prev === false ? prev : false));
      setInitialLoading((prev) => (prev === false ? prev : false));
      return undefined;
    }

    let cancelled = false;
    const loadFirstPage = async () => {
      setInitialLoading(true);
      loadingMoreRef.current = true;
      try {
        let sorted = [];
        let total = 0;
        let more = false;
        if (isVideoLibrary) {
          const res = await fetchVideoSpacePage(1, VIDEO_SPACE_LIST_PAGE_SIZE);
          const normalized = normalizeVideoSpacePage(res);
          sorted = filterPublishVideoLibraryRows(
            filterVideoSpaceByWorkspaceMode(normalized.rows, workspaceMode),
          );
          total = Math.max(normalized.total, sorted.length);
          more = normalized.hasMore && normalized.rows.length > 0;
        } else {
          const res = await fetchImageSpacePage(1, IMAGE_SPACE_PAGE_SIZE);
          const normalized = normalizeImageSpacePage(res);
          sorted = filterImageSpaceByWorkspaceMode(
            sanitizeImageSpaceRecords(normalized.rows),
            workspaceMode,
          );
          total = Math.max(normalized.total, sorted.length);
          more = normalized.hasMore && normalized.rows.length > 0;
        }
        if (cancelled) return;
        setPagedImages(sorted);
        setListTotal(total);
        setHasMore(more);
        pageRef.current = 1;
        syncAppendedToParent(sorted, { total, hasMore: more, page: 1 });
      } catch (error) {
        console.warn(isVideoLibrary ? '加载视频空间弹窗列表失败：' : '加载图片空间弹窗列表失败：', error);
        if (!cancelled) {
          const fallback = Array.isArray(images) ? images : [];
          setPagedImages(isVideoLibrary ? filterPublishVideoLibraryRows(fallback) : fallback);
          setListTotal(fallback.length);
          setHasMore(false);
          pageRef.current = fallback.length > 0 ? 1 : 0;
        }
      } finally {
        if (!cancelled) {
          loadingMoreRef.current = false;
          setInitialLoading(false);
        }
      }
    };

    loadFirstPage();
    return () => {
      cancelled = true;
    };
  // 仅随弹窗打开 / 工作区切换重置首屏；images 变化由 displayImages 合并
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 打开时快照 images 作失败回退
  }, [isOpen, workspaceMode, syncAppendedToParent, isVideoLibrary]);

  const loadMoreImages = useCallback(async () => {
    if (!isOpen || loadingMoreRef.current || !hasMore || initialLoading) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const nextPage = pageRef.current + 1;
      let sorted = [];
      let total = 0;
      let more = false;
      let rowsLen = 0;
      if (isVideoLibrary) {
        const res = await fetchVideoSpacePage(nextPage, VIDEO_SPACE_LIST_PAGE_SIZE);
        const normalized = normalizeVideoSpacePage(res);
        rowsLen = normalized.rows.length;
        sorted = filterPublishVideoLibraryRows(
          filterVideoSpaceByWorkspaceMode(normalized.rows, workspaceMode),
        );
        total = normalized.total;
        more = normalized.hasMore;
      } else {
        const res = await fetchImageSpacePage(nextPage, IMAGE_SPACE_PAGE_SIZE);
        const normalized = normalizeImageSpacePage(res);
        rowsLen = normalized.rows.length;
        sorted = filterImageSpaceByWorkspaceMode(
          sanitizeImageSpaceRecords(normalized.rows),
          workspaceMode,
        );
        total = normalized.total;
        more = normalized.hasMore;
      }
      pageRef.current = nextPage;
      if (rowsLen === 0) {
        setHasMore(false);
        return;
      }
      let appended = 0;
      if (sorted.length > 0) {
        setPagedImages((prev) => {
          const next = appendUniqueImageSpaceRows(prev, sorted);
          appended = next.length - prev.length;
          return next;
        });
        if (appended > 0) {
          syncAppendedToParent(sorted, {
            total,
            hasMore: more && appended > 0,
            page: nextPage,
          });
        }
      }
      if (total > 0) {
        setListTotal((prev) => Math.max(Number(prev) || 0, total));
      }
      // 视频格式过滤可能导致本页全被滤掉但仍有更多页
      setHasMore(Boolean(more && (sorted.length === 0 ? more : appended > 0 || isVideoLibrary)));
      if (isVideoLibrary && more && sorted.length === 0 && appended === 0) {
        setHasMore(true);
      }
    } catch (error) {
      console.warn(isVideoLibrary ? '视频空间弹窗加载更多失败：' : '图片空间弹窗加载更多失败：', error);
      setHasMore(false);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, initialLoading, isOpen, syncAppendedToParent, workspaceMode, isVideoLibrary]);

  useArmedScrollLoadMore({
    enabled: Boolean(isOpen && hasMore && !initialLoading && typeof loadMoreImages === 'function'),
    hasMore,
    loadingMore,
    onLoadMore: loadMoreImages,
    sentinelRef: loadMoreSentinelRef,
    scrollRoot: gridScrollRoot || bodyRef.current || null,
  });

  useLayoutEffect(() => {
    if (!isOpen || filteredImages.length === 0) return;
    // B3：仅 warm 列表已签 URL；可见区 SignedImage 自行懒加载 thumb
    warmDisplayCacheFromRecords(filteredImages, resolveRawImageUrl);
  }, [filteredImages, isOpen, resolveRawImageUrl]);

  const handleBuiltInUpload = useCallback(async (event) => {
    const files = Array.from(event?.target?.files ?? []);
    if (files.length === 0) return;
    event.target.value = '';
    try {
      const result = await uploadImageSpaceFiles({
        files,
        existingImages: images,
        uploadFolderId: uploadFolderId || '',
        uploadOriginSource: uploadOriginSource || 'upload',
        creatorPayload: creatorPayload || {},
      });
      if (result.enrichedNewImages?.length) {
        onUploadComplete?.(result.enrichedNewImages);
      }
      if (result.message && typeof showToast === 'function') {
        showToast(result.message, result.level || (result.ok ? 'success' : 'info'));
      }
    } catch (error) {
      const message = handleIpeError('image upload', error, t('studioToast.uploadFailedRetry'));
      if (typeof showToast === 'function') showToast(message, 'error');
    }
  }, [
    creatorPayload,
    images,
    onUploadComplete,
    showToast,
    t,
    uploadFolderId,
    uploadOriginSource,
  ]);

  const handleUploadChange = onUpload || (onUploadComplete ? handleBuiltInUpload : null);

  // 解析模版印花位：与工作流/POD 一致（stampAreas + 画布 ipelayer），避免只读 stampAreas 漏位
  useEffect(() => {
    if (selectedImageTemplates.length === 0) {
      setStampGroups((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const uniqueStampNames = resolveStampSlotNames(selectedImageTemplates);
    if (uniqueStampNames.length === 0) {
      setStampGroups((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const groups = uniqueStampNames.map((name) => ({
      name,
      originalName: name,
    }));
    setStampGroups((prev) => {
      if (
        prev.length === groups.length
        && prev.every((g, i) => g?.name === groups[i]?.name && g?.originalName === groups[i]?.originalName)
      ) {
        return prev;
      }
      return groups;
    });
  }, [selectedImageTemplates]);

  // 初始化位置数组：将 selection 转换为位置数组（禁止无意义的新 [] 引用，否则会 Maximum update depth）
  useEffect(() => {
    if (!isOpen) {
      setPositionArray((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    if (selection.length > 0) {
      setPositionArray((prev) => {
        if (
          prev.length === selection.length
          && prev.every((id, index) => id === selection[index])
        ) {
          return prev;
        }
        return [...selection];
      });
      return;
    }

    setPositionArray((prev) => (prev.length === 0 ? prev : []));
  }, [selection, isOpen]);

  // 滚动根节点：用 layout effect 同步，避免 callback ref 里 setState 在 StrictMode 下反复挂卸
  useLayoutEffect(() => {
    if (!isOpen) {
      setGridScrollRoot((prev) => (prev == null ? prev : null));
      return undefined;
    }
    const node = bodyRef.current;
    setGridScrollRoot((prev) => (prev === node ? prev : node));
    return undefined;
  }, [isOpen, initialLoading, filteredImages.length, listTotal]);

  // 加载列表缩略图 URL（仅随列表变化；禁止依赖 selection，否则点选会整页 IndexedDB + 全量重签导致卡死）
  useEffect(() => {
    if (!isOpen) return;

    const loadImageUrls = async () => {
      const urlMap = {};
      const existing = imageUrlMapRef.current || {};

      if (fromCropTask) {
        try {
          const { images: cropImages } = await cropImageAPI.getByTask(fromCropTask);
          const cropMap = new Map();
          (cropImages || []).forEach((img) => {
            cropMap.set(img.imageId, img);
          });
          displayImages.forEach((image) => {
            const cropData =
              cropMap.get(image.id)
              || cropMap.get(parseOriginalImageIdFromCropSpaceId(image.id));
            if (cropData?.croppedImageUrl) {
              urlMap[image.id] = cropData.croppedImageUrl;
            }
          });
        } catch (error) {
          console.warn('加载裁剪图片失败:', error);
        }
      }

      if (printUrlOverrides && typeof printUrlOverrides === 'object' && !Array.isArray(printUrlOverrides)) {
        displayImages.forEach((image) => {
          const u = resolveGoodsPrintUrl(image.id, printUrlOverrides);
          if (u) urlMap[image.id] = u;
        });
      }

      const imageIdsToLoad = [];
      displayImages.forEach((image) => {
        const id = image?.id;
        if (id == null) return;
        if (urlMap[id] || existing[id]) return;
        applyImageRowToUrlMap(image, urlMap);
        if (urlMap[id] || existing[id]) return;
        imageIdsToLoad.push(id);
      });

      if (imageIdsToLoad.length > 0) {
        await Promise.all(
          imageIdsToLoad.map(async (imageId) => {
            try {
              const url = await getImageFromIndexedDB(imageId);
              if (url) urlMap[imageId] = url;
            } catch (error) {
              console.warn(`无法加载图片 ${imageId}:`, error);
            }
          }),
        );
      }

      if (Object.keys(urlMap).length > 0) {
        setImageUrlMap((prev) => ({ ...prev, ...urlMap }));
      }
    };

    if (displayImages.length > 0) {
      loadImageUrls();
    }
  }, [displayImages, isOpen, fromCropTask, printUrlOverrides]);

  // 选中项兜底补 URL：只补尚未有地址的 id，避免点选触发整表重载
  useEffect(() => {
    if (!isOpen || selection.length === 0) return;

    const existing = imageUrlMapRef.current || {};
    const missingIds = selection.filter((imageId) => {
      if (imageId == null || existing[imageId]) return false;
      const image = displayImages.find((img) => String(img.id) === String(imageId));
      if (image) {
        const probe = {};
        applyImageRowToUrlMap(image, probe);
        if (probe[imageId] || probe[image.id]) return false;
      }
      if (printUrlOverrides) {
        const u = resolveGoodsPrintUrl(imageId, printUrlOverrides);
        if (u) return false;
      }
      return true;
    });
    if (missingIds.length === 0) return;

    let cancelled = false;
    (async () => {
      const urlMap = {};
      await Promise.all(
        missingIds.map(async (imageId) => {
          try {
            const url = await getImageFromIndexedDB(imageId);
            if (url) urlMap[imageId] = url;
          } catch (error) {
            console.warn(`无法加载选中图片 ${imageId}:`, error);
          }
        }),
      );
      if (!cancelled && Object.keys(urlMap).length > 0) {
        setImageUrlMap((prev) => ({ ...prev, ...urlMap }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selection, isOpen, printUrlOverrides, displayImages]);

  // 仅在弹窗关闭/卸载时 revoke blob，禁止随 imageUrlMap 每次合并误销仍在展示的 ObjectURL
  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      Object.values(imageUrlMapRef.current).forEach((url) => {
        if (url && String(url).startsWith('blob:')) {
          try {
            URL.revokeObjectURL(url);
          } catch (_) {
            // ignore
          }
        }
      });
    };
  }, [isOpen]);

  const markImageLoadFailed = useCallback((imageId) => {
    if (!imageLoadGuardRef.current.markFailed(imageId)) return;
    setFailedImageIds(imageLoadGuardRef.current.snapshotFailed());
  }, []);

  const selectionLimit = computeLibrarySelectionLimit(positionArray, maxSelection, outsideSelectedIds);
  const { modalCount, effectiveTotal, canSelectMore, displayMax } = selectionLimit;
  const selectedIdSet = useMemo(
    () => new Set(positionArray.filter((id) => id != null).map((id) => String(id))),
    [positionArray],
  );

  const lockCloseUntilSelection = requireSelectionBeforeClose != null
    ? Boolean(requireSelectionBeforeClose)
    : selectedImageTemplates.length > 0;
  const assignedImageCount = getModalSelectedIds(positionArray).length;

  const handleRequestClose = useCallback(() => {
    if (lockCloseUntilSelection && assignedImageCount === 0) {
      const message = stampGroups.length > 0
        ? t('studioChrome.libraryStampAssignBeforeClose')
        : t('studioChrome.librarySelectBeforeClose');
      if (typeof showToast === 'function') {
        showToast(message, 'warning');
      }
      return;
    }
    onClose?.();
  }, [
    assignedImageCount,
    lockCloseUntilSelection,
    onClose,
    showToast,
    stampGroups.length,
    t,
  ]);

  useEffect(() => {
    if (!isOpen || !closeOnBackdropClick) return undefined;
    if (!lockCloseUntilSelection || assignedImageCount > 0) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handleRequestClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [assignedImageCount, closeOnBackdropClick, handleRequestClose, isOpen, lockCloseUntilSelection]);

  // 图片加载错误：每图最多一次本地恢复；禁止对行内 viewUrl 反复 resolveSignedDisplayUrl（会打满 sign/view → 429 频闪）
  const handleImageError = useCallback(async (e, imageId) => {
    const gate = imageLoadGuardRef.current.beginRecovery(imageId);
    if (!gate.proceed) {
      if (gate.markedFailed) {
        setFailedImageIds(imageLoadGuardRef.current.snapshotFailed());
      }
      return;
    }

    const currentSrc = String(e?.currentTarget?.currentSrc || e?.target?.currentSrc || e?.target?.src || '').trim();
    const applyRecovered = (displaySrc) => {
      if (!shouldApplyRecoveredImageUrl(currentSrc, displaySrc)) {
        markImageLoadFailed(gate.id);
        return false;
      }
      setImageUrlMap((prev) => ({ ...prev, [gate.id]: displaySrc }));
      return true;
    };

    try {
      const override = resolveGoodsPrintUrl(gate.id, printUrlOverrides || {});
      if (override) {
        // 仅当 override 与当前失败 src 不是同一串时再签一次；同源失败直接放弃
        if (!shouldApplyRecoveredImageUrl(currentSrc, override)) {
          markImageLoadFailed(gate.id);
          return;
        }
        const displaySrc = await resolveSignedDisplayUrl(override);
        if (applyRecovered(displaySrc)) return;
        return;
      }

      // 行内已有 view/display：SignedImage 自身会续签；父级再 resolve/setState 只会放大请求风暴。
      // 此处仅消耗一次 beginRecovery；若续签后再次 onError，gate 会永久 markFailed 并卸图。
      const image = displayImages.find((img) => String(img.id) === String(gate.id));
      if (pickImageSpaceDisplayUrl(image)) {
        return;
      }

      const url = await getImageFromIndexedDB(gate.id);
      if (url) {
        const displaySrc = (url.startsWith('data:') || url.startsWith('blob:'))
          ? url
          : await resolveSignedDisplayUrl(url, image || {});
        if (applyRecovered(displaySrc)) return;
        return;
      }
      markImageLoadFailed(gate.id);
    } catch (error) {
      console.warn(`重新加载图片 ${gate.id} 失败:`, error);
      markImageLoadFailed(gate.id);
    }
  }, [displayImages, markImageLoadFailed, printUrlOverrides]);

  if (!isOpen) return null;

  // 处理清除：将对应位置设为null，并取消选择
  const handleClearPosition = (positionIndex) => {
    const imageId = positionArray[positionIndex];
    if (imageId) {
      // 将位置设为null
      setPositionArray((prev) => {
        const newArray = [...prev];
        newArray[positionIndex] = null;
        return newArray;
      });
      // 取消选择
      onToggle(imageId);
    }
  };

  const notifySelectionLimitReached = () => {
    if (typeof onSelectionLimitReached === 'function') {
      onSelectionLimitReached();
    }
  };

  // 处理选择：优先填充空位置（保持普通函数：位于 isOpen 早退后，不能用 hooks）
  const handleToggleWithPosition = (imageId) => {
    const id = imageId != null ? String(imageId) : '';
    if (!id) return;
    const isSelected = selectedIdSet.has(id);
    if (isSelected) {
      setPositionArray((prev) => {
        const positionIndex = prev.findIndex((item) => item != null && String(item) === id);
        if (positionIndex < 0) return prev;
        const next = [...prev];
        next[positionIndex] = null;
        return next;
      });
      onToggle?.(imageId);
      return;
    }
    if (!canSelectMore) {
      notifySelectionLimitReached();
      return;
    }
    setPositionArray((prev) => {
      const emptyIndex = prev.findIndex((item) => item === null || item === undefined);
      if (emptyIndex >= 0) {
        const next = [...prev];
        next[emptyIndex] = imageId;
        return next;
      }
      return [...prev, imageId];
    });
    onToggle?.(imageId);
  };

  // 全选/取消全选：仅作用于当前已加载
  const loadedIds = filteredImages.map((image) => image.id);
  const allSelected =
    loadedIds.length > 0 && loadedIds.every((id) => positionArray.includes(id));
  const handleSelectAll = () => {
    const loadedIdSet = new Set(loadedIds);
    if (allSelected) {
      const toDeselect = positionArray.filter((imageId) => imageId && loadedIdSet.has(imageId));
      setPositionArray((prev) => prev.map((imageId) => (imageId && loadedIdSet.has(imageId) ? null : imageId)));
      toDeselect.forEach((imageId) => onToggle(imageId));
    } else {
      const newPositionArray = [...positionArray];
      let positionIndex = 0;
      let limitReachedNotified = false;
      filteredImages.forEach((image) => {
        if (positionArray.includes(image.id)) return;
        const limitState = computeLibrarySelectionLimit(newPositionArray, maxSelection, outsideSelectedIds);
        if (!limitState.canSelectMore) {
          if (!limitReachedNotified) {
            notifySelectionLimitReached();
            limitReachedNotified = true;
          }
          return;
        }
        while (
          positionIndex < newPositionArray.length
          && newPositionArray[positionIndex] !== null
          && newPositionArray[positionIndex] !== undefined
        ) {
          positionIndex++;
        }
        if (positionIndex < newPositionArray.length) {
          newPositionArray[positionIndex] = image.id;
        } else {
          newPositionArray.push(image.id);
        }
        positionIndex++;
        onToggle(image.id);
      });
      setPositionArray(newPositionArray);
    }
  };

  const handleToggleDateGroup = (groupItems) => {
    const ids = groupItems.map((image) => image.id).filter(Boolean);
    if (ids.length === 0) return;

    const allGroupSelected = ids.every((id) => positionArray.includes(id));
    if (allGroupSelected) {
      const idSet = new Set(ids);
      const toDeselect = positionArray.filter((imageId) => imageId && idSet.has(imageId));
      setPositionArray((prev) => prev.map((imageId) => (imageId && idSet.has(imageId) ? null : imageId)));
      toDeselect.forEach((imageId) => onToggle(imageId));
      return;
    }

    const newPositionArray = [...positionArray];
    let positionIndex = 0;
    groupItems.forEach((image) => {
      if (!positionArray.includes(image.id)) {
        while (
          positionIndex < newPositionArray.length
          && newPositionArray[positionIndex] !== null
          && newPositionArray[positionIndex] !== undefined
        ) {
          positionIndex++;
        }
        if (positionIndex < newPositionArray.length) {
          newPositionArray[positionIndex] = image.id;
        } else {
          newPositionArray.push(image.id);
        }
        positionIndex++;
        onToggle(image.id);
      }
    });
    setPositionArray(newPositionArray);
  };

  return (
    <>
    <div className="funding-modal funding-modal--stack-top">
      <div
        className="funding-modal__backdrop"
        role="presentation"
        onClick={closeOnBackdropClick ? handleRequestClose : undefined}
      />
      <div
        className={`funding-modal__panel funding-modal__panel--unified funding-modal__panel--library${panelClassName ? ` ${panelClassName}` : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <header className="funding-modal__header funding-library-modal__header">
          <div className="funding-library-modal__header-left">
            <label className="funding-library-modal__select-all">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                disabled={filteredImages.length === 0}
              />
              <span>{selectAllLabel || t('studioChrome.librarySelectAllLoaded')}</span>
            </label>
            <p className="funding-library-modal__header-hint">
              {displayMax != null
                ? t('studioChrome.librarySelectedMaxHint', { count: effectiveTotal, max: displayMax })
                : t('studioChrome.librarySelectedHint', { count: modalCount })}
            </p>
          </div>
          <h3 className="funding-library-modal__header-title">
            {isVideoLibrary ? t('studioChrome.libraryVideoSpace') : t('studioChrome.libraryImageSpace')}
          </h3>
          <div className="funding-library-modal__header-right">
            <LibraryModalDateFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={(start, end) => {
                setFilterStartDate(start || '');
                setFilterEndDate(end || '');
              }}
            />
            {!isVideoLibrary ? (
            <div className="funding-library-modal__source-filter">
              <span className="funding-library-modal__source-label">{t('studioChrome.librarySourceLabel')}</span>
              <IpeToolbarSelect
                id="funding-library-modal-source-filter"
                className="funding-library-modal__source-select"
                value={sourceFilter}
                onChange={setSourceFilter}
                options={buildImageSpaceLibraryModalSourceOptions()}
                ariaLabel={t('studioChrome.librarySourceFilterAria')}
                variant="light"
                clearable={false}
                width={132}
              />
            </div>
            ) : null}
            {handleUploadChange && !isVideoLibrary ? (
              <label className="funding-library-modal__upload-btn">
                <input
                  type="file"
                  accept={IPE_UPLOAD_ACCEPT}
                  multiple
                  onChange={handleUploadChange}
                  style={{ display: 'none' }}
                />
                {t('studioChrome.upload')}
              </label>
            ) : null}
            {isVideoLibrary ? (
              <span className="funding-library-modal__header-hint">
                {t('studioChrome.libraryVideoFormatsHint')}
              </span>
            ) : null}
            {filteredImages.length > 0 || listTotal > 0 ? (
              <span className="funding-library-modal__header-hint">
                {listTotal > filteredImages.length
                  ? t('studioChrome.libraryLoadedCount', { loaded: filteredImages.length, total: listTotal })
                  : t('studioChrome.libraryTotalCount', { total: listTotal > 0 ? listTotal : filteredImages.length })}
              </span>
            ) : null}
          </div>
        </header>
        <div className={`funding-library-modal__body ${selectedImageTemplates.length > 0 && stampGroups.length > 0 && selection.length > 0 ? 'funding-library-modal__body--with-stamps' : 'funding-library-modal__body--without-stamps'}`}>
          {/* 图片网格区域 - 可滚动，保持固定高度 */}
          <div
            className="funding-library-modal__grid-container"
            ref={bodyRef}
          >
            {initialLoading && displayImages.length === 0 ? (
              <p className="funding-library-modal__empty">{t('studioChrome.loading')}</p>
            ) : displayImages.length > 0 ? (
              filteredImages.length > 0 ? (
                <>
                  <LibraryModalDateGroupedGrid
                    items={filteredImages}
                    isItemSelected={(item) => selectedIdSet.has(String(item?.id))}
                    onToggleDateGroup={handleToggleDateGroup}
                    renderItem={(image) => {
                      const isSelected = selectedIdSet.has(String(image.id));
                      const isAtLimit = displayMax != null && !canSelectMore && !isSelected;
                      const loadFailed = failedImageIds.has(String(image.id));
                      return (
                        <LibraryModalImageCard
                          key={image.id}
                          image={image}
                          urlMap={imageUrlMap}
                          printUrlOverrides={printUrlOverrides}
                          scrollRoot={gridScrollRoot}
                          selected={isSelected}
                          loadFailed={loadFailed}
                          onToggle={(imageId) => {
                            if (isAtLimit) {
                              notifySelectionLimitReached();
                              return;
                            }
                            handleToggleWithPosition(imageId);
                          }}
                          onPreview={setPreviewTarget}
                          onImageError={(e) => handleImageError(e, image.id)}
                        />
                      );
                    }}
                  />
                  <IpeLazyListSentinel
                    sentinelRef={loadMoreSentinelRef}
                    loadingMore={loadingMore}
                    hasMore={hasMore}
                    loadedCount={displayImages.length}
                    totalItems={listTotal > 0 ? listTotal : displayImages.length}
                  />
                </>
              ) : (
                <p className="funding-library-modal__empty">{t('studioChrome.libraryEmptyRange')}</p>
              )
            ) : (
              <p className="funding-library-modal__empty">
                {isVideoLibrary
                  ? t('studioChrome.libraryEmptyVideo')
                  : t('studioChrome.libraryEmptyImage')}
              </p>
            )}
          </div>

          {/* 主图创建模式：底部印花区域 - 固定在底部 */}
          {selectedImageTemplates.length > 0 && stampGroups.length > 0 && selection.length > 0 && (
            <div className="funding-library-modal__stamp-area">
              <div className="funding-library-modal__stamp-title">
                {t('studioChrome.libraryStampAssignTitle')}
              </div>
              <div className="funding-library-modal__stamp-grid">
                {(() => {
                  // 计算需要显示的组数：至少显示1组，根据位置数组的长度动态增加
                  // 例如：2个印花区域，0-2个位置 -> 1组，3-4个位置 -> 2组，5-6个位置 -> 3组
                  const groupCount = Math.max(1, Math.ceil(positionArray.length / stampGroups.length));

                  // 生成所有组
                  const groups = [];
                  for (let groupIndex = 0; groupIndex < groupCount; groupIndex++) {
                    const groupSquares = [];
                    stampGroups.forEach((stampGroup, stampIndex) => {
                      // 计算这个组中这个印花区域应该显示的图片索引
                      const positionIndex = groupIndex * stampGroups.length + stampIndex;

                      // 获取分配给这个位置的图片（如果存在）
                      const assignedImageId = positionIndex < positionArray.length ? positionArray[positionIndex] : null;
                      const assignedImage = assignedImageId ? displayImages.find((img) => img.id === assignedImageId) : null;

                      // 处理清除功能
                      const handleClear = (e) => {
                        e.stopPropagation();
                        handleClearPosition(positionIndex);
                      };

                      groupSquares.push(
                        <div
                          key={`group-${groupIndex}-stamp-${stampIndex}`}
                          className="funding-library-modal__stamp-item"
                        >
                          {/* 正方形区域 */}
                          <div className="funding-library-modal__stamp-square">
                            {assignedImage ? (
                              (() => {
                                const imageUrl = resolveThumbUrl(assignedImage.id, assignedImage) || null;
                                const loadFailed = failedImageIds.has(String(assignedImage.id));

                                return imageUrl && !loadFailed ? (
                                  <>
                                    <WorkbenchPreviewImage
                                      url={imageUrl}
                                      record={assignedImage}
                                      displayUrl={assignedImage.displayUrl || assignedImage.viewUrl}
                                      alt=""
                                      viewRoot={gridScrollRoot}
                                      className="funding-library-modal__stamp-image"
                                      onError={(e) => handleImageError(e, assignedImage.id)}
                                    />
                                    <button
                                      type="button"
                                      className="funding-library-modal__stamp-remove"
                                      onClick={handleClear}
                                      aria-label={t('studioChrome.libraryClearImageAria')}
                                    >
                                      ×
                                    </button>
                                  </>
                                ) : (
                                  <span className="funding-library-modal__stamp-loading">
                                    {loadFailed ? t('studioChrome.libraryLoadFailed') : t('studioChrome.libraryLoadingDots')}
                                  </span>
                                );
                              })()
                            ) : null}
                          </div>
                          {/* 印花名称 */}
                          <span className="funding-library-modal__stamp-name">
                            {stampGroup.name}
                          </span>
                        </div>
                      );
                    });

                    // 为每组添加包装div
                    groups.push(
                      <div
                        key={`group-${groupIndex}`}
                        className="funding-library-modal__stamp-group"
                      >
                        {groupSquares}
                      </div>
                    );
                  }

                  return groups;
                })()}
              </div>
            </div>
          )}
        </div>
        <footer className="funding-modal__footer funding-library-modal__footer">
          {lockCloseUntilSelection && assignedImageCount === 0 ? (
            <p className="funding-library-modal__footer-hint">
              {t('studioChrome.libraryStampAssignRequired')}
            </p>
          ) : (
            <span className="funding-library-modal__footer-spacer" aria-hidden="true" />
          )}
          <div className="funding-library-modal__footer-actions">
            <button
              type="button"
              className="funding-button funding-button--ghost"
              onClick={handleRequestClose}
            >
              {t('studioChrome.cancel')}
            </button>
            <button
              type="button"
              className="funding-button funding-button--accent"
              onClick={async () => {
              // 将位置数组转换为ID数组（过滤掉null值）
              const finalSelection = positionArray.filter((id) => id !== null && id !== undefined);
              // 更新selection以匹配位置数组
              finalSelection.forEach((imageId) => {
                if (!selection.includes(imageId)) {
                  onToggle(imageId);
                }
              });
              selection.forEach((imageId) => {
                if (!finalSelection.includes(imageId)) {
                  onToggle(imageId);
                }
              });
              const slots = positionArray.filter((id) => id !== null && id !== undefined);
              if (!deferUsageBump) {
                try {
                  const knownIds = new Set((displayImages || []).map((img) => String(img?.id || '')).filter(Boolean));
                  const bumpableSlots = slots.filter((id) => knownIds.has(String(id)));
                  if (bumpableSlots.length > 0) {
                    const deltaMap = await bumpImageSpaceUsageCounts(bumpableSlots);
                    if (onUsageBumped && deltaMap && Object.keys(deltaMap).length > 0) {
                      onUsageBumped(deltaMap);
                    }
                  }
                } catch (e) {
                  console.warn('[LibraryModal] bump usage', e);
                }
              }
              // 调用确认回调，传递位置数组和印花分组
              onConfirm(positionArray, stampGroups);
            }}
            disabled={positionArray.filter((id) => id !== null && id !== undefined).length === 0 || displayImages.length === 0}
          >
            {t('studioChrome.libraryConfirmUse')}
          </button>
          </div>
        </footer>
      </div>
    </div>
    {isVideoLibrary ? (
      <VideoSpacePreviewModal
        open={!!previewTarget}
        video={previewTarget}
        navList={filteredImages}
        onClose={() => setPreviewTarget(null)}
      />
    ) : (
      <ImageSpacePreviewModal
        open={!!previewTarget}
        initialImage={previewTarget}
        navList={filteredImages}
        onClose={() => setPreviewTarget(null)}
        resolveUrl={(id, img) => resolveThumbUrl(id, img)}
        authUserId={authUserId}
        authDisplayName={authDisplayName}
      />
    )}
    </>
  );
}
